'use client';

import {
  RampAdUnit,
  RampRuntime,
  RampSpaAdsOptions,
} from '@/features/ads/shared/types/playwire-runtime';

export type { RampAdUnit, RampRuntime, RampSpaAdsOptions };

export function getRamp(): RampRuntime | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.ramp ?? null;
}

export function ensureRampQueue(): RampRuntime {
  const ramp = (window.ramp ??= {});
  ramp.que ??= [];
  return ramp;
}

export function queueRampCommand(command: (ramp: RampRuntime) => void): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const ramp = ensureRampQueue();
  ramp.que?.push(() => {
    command(ramp);
  });

  return true;
}

export function queueSpaAds(options: RampSpaAdsOptions): boolean {
  return queueRampCommand((ramp) => {
    if (typeof ramp.spaAds !== 'function') {
      return;
    }

    ramp.spaAds(options);
  });
}

function runSpaAddAds(ramp: RampRuntime, ads: RampAdUnit[]): Promise<void> {
  return Promise.resolve().then(() => {
    if (typeof ramp.spaAddAds !== 'function') {
      throw new Error('Ramp spaAddAds is unavailable');
    }

    return ramp.spaAddAds(ads);
  });
}

export function queueSpaAddAds(ads: RampAdUnit[], ramp?: RampRuntime): Promise<void> {
  if (ramp) {
    return runSpaAddAds(ramp, ads);
  }

  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Ramp queue is unavailable'));
  }

  return new Promise<void>((resolve, reject) => {
    const queued = queueRampCommand((queuedRamp) => {
      void runSpaAddAds(queuedRamp, ads).then(resolve, reject);
    });

    if (!queued) {
      reject(new Error('Ramp queue is unavailable'));
    }
  });
}
